import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Meteor } from 'meteor/meteor';
import L from 'leaflet';
import i18n, { currentLocale } from '../../../i18n';
import 'leaflet/dist/leaflet.css';
import type { Outlet } from '../types';
import { Icon } from './shared/Icon';

type OutletEvent = NonNullable<Outlet['events']>[number];
type LatLng = { lat: number; lng: number };

function fmtDist(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`;
}

// Haversine distance in metres between two coordinates.
function haversine(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

const REST_SVG = `<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11l9-7 9 7M5 10v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10"/><path d="M9 21v-6h6v6"/></svg>`;
const VENUE_SVG = `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z"/></svg>`;

function LiveMap({
  rest,
  venue,
  surge,
}: {
  rest: LatLng;
  venue: LatLng | null;
  surge: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return undefined;
    }

    const map = L.map(el, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(map);

    const restIcon = L.divIcon({
      className: 'emap-mk-wrap',
      html: `<div class="emap-mk emap-mk-rest"><span class="emap-mk-ring"></span>${REST_SVG}</div>`,
      iconSize: [34, 34],
      iconAnchor: [17, 17],
    });
    L.marker([rest.lat, rest.lng], { icon: restIcon, keyboard: false })
      .addTo(map)
      .bindTooltip(i18n.t('live:eventMap.yourRestaurant'), {
        direction: 'top',
        offset: [0, -14],
      });

    let raf = 0;
    if (venue) {
      const venueIcon = L.divIcon({
        className: 'emap-mk-wrap',
        html: `<div class="emap-mk emap-mk-venue">${VENUE_SVG}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });
      L.marker([venue.lat, venue.lng], { icon: venueIcon, keyboard: false })
        .addTo(map)
        .bindTooltip(i18n.t('live:eventMap.eventVenue'), {
          direction: 'top',
          offset: [0, -12],
        });

      L.polyline(
        [
          [venue.lat, venue.lng],
          [rest.lat, rest.lng],
        ],
        {
          className: 'emap-leaflet-route',
          weight: 3,
          dashArray: '4 8',
          interactive: false,
        },
      ).addTo(map);

      // Animated crowd streaming from the venue toward the restaurant.
      const count = surge ? 11 : 7;
      const period = surge ? 2400 : 3600;
      const dotIcon = L.divIcon({
        className: 'emap-dot-wrap',
        html: '<span class="emap-dot"></span>',
        iconSize: [10, 10],
        iconAnchor: [5, 5],
      });
      const dots = Array.from({ length: count }, () =>
        L.marker([venue.lat, venue.lng], {
          icon: dotIcon,
          interactive: false,
          keyboard: false,
        }).addTo(map),
      );

      const start = performance.now();
      const tick = (now: number) => {
        const elapsed = now - start;
        dots.forEach((m, i) => {
          const phase = ((elapsed / period + i / count) % 1 + 1) % 1;
          m.setLatLng([
            venue.lat + (rest.lat - venue.lat) * phase,
            venue.lng + (rest.lng - venue.lng) * phase,
          ]);
          const node = m.getElement();
          if (node) {
            node.style.opacity = String(
              phase < 0.12
                ? phase / 0.12
                : phase > 0.88
                  ? (1 - phase) / 0.12
                  : 1,
            );
          }
        });
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);

      map.fitBounds(
        [
          [venue.lat, venue.lng],
          [rest.lat, rest.lng],
        ],
        { padding: [48, 48], maxZoom: 16 },
      );
    } else {
      map.setView([rest.lat, rest.lng], 15);
    }

    // The popup animates in, so the container has no size at first paint.
    const t1 = setTimeout(() => map.invalidateSize(), 60);
    const t2 = setTimeout(() => map.invalidateSize(), 320);

    return () => {
      if (raf) {
        cancelAnimationFrame(raf);
      }
      clearTimeout(t1);
      clearTimeout(t2);
      map.remove();
    };
  }, [rest.lat, rest.lng, venue?.lat, venue?.lng, surge, currentLocale()]);

  return <div className="emap-map" ref={ref} />;
}

function geocode(address?: string, bias?: LatLng | null): Promise<LatLng | null> {
  if (!address) {
    return Promise.resolve(null);
  }
  return Meteor.callPromise(
    'opsCommand/geocodeAddress',
    address,
    bias ?? undefined,
  )
    .then((res: LatLng | null) => res ?? null)
    .catch(() => null);
}

export function EventMapPopup({
  event,
  outletName,
  restLat,
  restLng,
  restAddress,
  onClose,
}: {
  event: OutletEvent;
  outletName: string;
  restLat?: number;
  restLng?: number;
  restAddress?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('live');
  const cityRest: LatLng | null =
    restLat != null && restLng != null
      ? { lat: restLat, lng: restLng }
      : null;

  const [rest, setRest] = useState<LatLng | null>(cityRest);
  const [venue, setVenue] = useState<LatLng | null>(
    event.lat != null && event.lng != null
      ? { lat: event.lat, lng: event.lng }
      : null,
  );
  const venueName =
    event.type && event.type !== 'Local event' ? event.type : undefined;
  const venuePreset = event.lat != null && event.lng != null;
  const [locating, setLocating] = useState<boolean>(
    !!((!cityRest && restAddress) || (!venuePreset && (event.address || venueName))),
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;

    // Reject venues implausibly far from the restaurant - "nearby events"
    // shouldn't be on another continent, so a bad geocode is treated as a miss.
    const MAX_VENUE_KM = 250;
    const plausible = (p: LatLng | null): LatLng | null => {
      if (!p) {
        return null;
      }
      if (cityRest && haversine(cityRest, p) > MAX_VENUE_KM * 1000) {
        return null;
      }
      return p;
    };

    // Resolve the venue: prefer the precise street address, then fall back to
    // the venue name (both biased toward the restaurant to disambiguate).
    const resolveVenue = async (): Promise<LatLng | null> => {
      if (venuePreset) {
        return null;
      }
      const byAddress = plausible(await geocode(event.address, cityRest));
      if (byAddress) {
        return byAddress;
      }
      return plausible(await geocode(venueName, cityRest));
    };

    // The server already provides the precise restaurant coordinate; only
    // geocode the address as a fallback when it's missing. This keeps the pin
    // (and therefore the popup distance) identical to the value in the list.
    const resolveRest = async (): Promise<LatLng | null> =>
      cityRest ?? geocode(restAddress, null);

    Promise.all([resolveRest(), resolveVenue()])
      .then(([restGeo, venueGeo]) => {
        if (cancelled) {
          return;
        }
        if (restGeo) {
          setRest(restGeo);
        }
        if (!venuePreset) {
          setVenue(venueGeo);
        }
        setLocating(false);
      })
      .catch(() => {
        if (!cancelled) {
          setLocating(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [restAddress, event.address, event.lat, event.lng]);

  const realDist =
    rest && venue ? Math.round(haversine(rest, venue)) : event.dist;

  return (
    <>
      <div className="scrim" onClick={onClose} role="presentation" />
      <div
        className="event-map-modal"
        role="dialog"
        aria-label={t('eventMap.eventAria', { name: event.name })}
      >
        <div className="emap-head">
          <div className="emap-title">
            <div className="emap-icon">
              <Icon name="ticket" size={16} />
            </div>
            <div>
              <h2>{event.name}</h2>
              <p>
                {t('eventMap.awayHeading', {
                  time: event.start,
                  dist: fmtDist(realDist),
                })}{' '}
                <b>{outletName}</b>
              </p>
            </div>
          </div>
          <div className="emap-head-right">
            {event.surge && (
              <span className="emap-surge">
                <Icon name="bolt" size={12} /> {t('eventMap.surgeExpected')}
              </span>
            )}
            <button
              type="button"
              className="ghost-btn"
              onClick={onClose}
              aria-label={t('eventMap.close')}
            >
              <Icon name="close" size={18} />
            </button>
          </div>
        </div>

        <div className="emap-canvas">
          {rest ? (
            <>
              {locating && (
                <div className="emap-locating">{t('eventMap.locating')}</div>
              )}
              <LiveMap rest={rest} venue={venue} surge={event.surge} />
              {!locating && !venue && (
                <div className="emap-note">
                  {t('eventMap.venueMiss')}
                </div>
              )}
            </>
          ) : (
            <div className="emap-note emap-note-block">
              {t('eventMap.mapUnavailable')}
            </div>
          )}
        </div>

        <div className="emap-foot">
          <div className="emap-stat">
            <span className="emap-stat-l">{t('eventMap.venue')}</span>
            <span className="emap-stat-v">{event.type}</span>
          </div>
          <div className="emap-stat">
            <span className="emap-stat-l">{t('eventMap.starts')}</span>
            <span className="emap-stat-v">{event.start}:00</span>
          </div>
          <div className="emap-stat">
            <span className="emap-stat-l">{t('eventMap.distance')}</span>
            <span className="emap-stat-v">{fmtDist(realDist)}</span>
          </div>
          <div className="emap-stat">
            <span className="emap-stat-l">{t('eventMap.demand')}</span>
            <span
              className="emap-stat-v"
              style={{ color: event.surge ? 'var(--accent)' : 'var(--ink-2)' }}
            >
              {event.surge
                ? t('eventMap.walkInSurge')
                : typeof event.impact === 'number'
                  ? t('eventMap.lift', { pct: event.impact })
                  : t('eventMap.steady')}
            </span>
          </div>
        </div>
        {event.address && (
          <div className="emap-address">
            <Icon name="pin" size={13} /> {event.address}
          </div>
        )}
      </div>
    </>
  );
}
